import {
  FluidFlexAlignment,
  FluidFlexDirection,
  FluidFlexJustification,
  FluidSize,
  generateUniqueId,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Host,
  Prop,
} from '@stencil/core';

import {
  GRID_ALIGN_ITEMS_VALIDATION,
  GRID_CHILDREN,
  GRID_CHILDREN_TYPE,
  GRID_JUSTIFY_VALIDATION,
} from '../../constants/constants';
import { Utils } from '../../utils/utils';

/**
 * @displayName Grid
 *
 * @contextData FLUID Grid is a responsive, flexbox-based component that can be used to build layouts of all shapes & sizes. Grid is meant to be used with FLUID Grid Item children - together, they provide a 12-column grid framework that is customizable at 5 incremental breakpoints.
 *
 * @slot - Grid Items displayed within the Grid. NOTE: slotted children MUST be FLUID Grid Items
 */
@Component({
  tag: 'fluid-grid',
  styleUrl: 'fluid-grid.less',
})
export class FluidGrid implements ComponentInterface {
  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidGridElement;

  // ================================================================ //
  // -- Props

  /**
   * When true, Grid will expand vertically to fill 100% of parent
   * container height.
   */
  @Prop({ mutable: true }) fullHeight = false;

  /**
   * When true, Grid will display as "inline-flex" element.
   */
  @Prop({ mutable: true }) inline = false;

  /**
   * Sets spacing/gap size between Grid Item children
   * (xxs: 8px | xs: 16px | sm: 24px | md: 32px | lg: 48px | xl: 64px)
   */
  @Prop({ mutable: true }) gridItemSpacing: Exclude<FluidSize, FluidSize.FULL> =
    FluidSize.EXTRA_SMALL;

  /**
   * When true, removes default spacing between Grid Item children.
   */
  @Prop({ mutable: true }) removeSpacing = false;

  /**
   * Sets horizontal alignment of Grid Item children. Only applicable
   * when the sum of all Grid Item "col-..." values is less than 12 OR
   * "auto" Prop is used & the width of all Grid Item children is less
   * than the total width of the parent Grid.
   */
  @Prop({ mutable: true }) justify: FluidFlexJustification;

  /**
   * Sets vertical alignment of Grid Item children. Only applicable if
   * Grid Item children have uneven heights, or if parent Grid height is
   * greater than the height of its children.
   */
  @Prop({ mutable: true }) alignItems: FluidFlexAlignment;

  // ================================================================ //
  // -- Testing Enablement

  /**
   * When supplied, will be prepended on each inner element of the grid in order
   * to help facilitate testing.
   */
  @Prop({ mutable: true }) identifier: string;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action validateJustify - validates "justify" Prop value
   * @action validateAlignItems - validates "alignItems" Prop value
   * @action validateSpacing - validates "gridItemSpacing" Prop value
   */
  connectedCallback() {
    this.validateJustify();
    this.validateAlignItems();
    this.validateSpacing();
    this.identifier = this.identifier ?? this.host.id ?? generateUniqueId();
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action validateChildren - validates slotted children
   */
  componentWillLoad(): void {
    this.validateChildren();
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Validates that Grid has at least 1 Grid Item child AND validates
   * that all Grid children are Grid Item components.
   */
  validateChildren(): void {
    const gridItems = Array.from(this.host.children);

    if (!gridItems.length) {
      Utils.consoleWarn(this.host, GRID_CHILDREN);
    } else if (
      !gridItems.every((gridItem) => gridItem.localName === 'fluid-grid-item')
    ) {
      Utils.consoleWarn(this.host, GRID_CHILDREN_TYPE);
    }
  }

  /**
   * Validates that "justify" Prop has a valid FluidFlexJustification
   * value (if "justify" Prop is provided). If value is NOT valid,
   * resets to default (i.e. "undefined").
   */
  validateJustify(): void {
    if (
      isNullOrUndefined(this.justify) ||
      Utils.isFluidEnum<FluidFlexJustification>(
        this.justify,
        FluidFlexJustification
      )
    )
      return;
    Utils.consoleWarn(this.host, GRID_JUSTIFY_VALIDATION(this.justify));
    this.justify = undefined;
  }

  /**
   * Validates that "alignItems" Prop has a valid FluidFlexAlignment
   * value (if "alignItems" Prop is provided). If value is NOT valid,
   * resets to default (i.e. "undefined").
   */
  validateAlignItems(): void {
    if (
      isNullOrUndefined(this.alignItems) ||
      Utils.isFluidEnum<FluidFlexAlignment>(this.alignItems, FluidFlexAlignment)
    )
      return;
    Utils.consoleWarn(this.host, GRID_ALIGN_ITEMS_VALIDATION(this.alignItems));
    this.alignItems = undefined;
  }

  /**
   * If deprecated "gutter" Prop is used, outputs deprecation warning &
   * updates "removeSpacing" Prop accordingly.
   */
  validateSpacing(): void {
    if (!this.gutter) {
      Utils.deprecationWarning(
        this.host,
        'Prop',
        'gutter',
        'removeSpacing',
        'boolean'
      );
      this.removeSpacing = true;
    }
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <Host
        class={{
          'full-height': this.fullHeight,
          'inline-flex': this.inline,
          'no-spacing': this.removeSpacing,
          [`spacing-${this.gridItemSpacing}`]: !this.removeSpacing,
          [`justify-${this.justify}`]: !!this.justify,
          [`align-${this.alignItems}`]: !!this.alignItems,
          [`direction-${this.direction}`]: !!this.direction,
        }}
        data-test-id={`${this.identifier}-grid`}>
        <slot />
      </Host>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * Set the direction of the grid for how items are rendered in a
   * horizontal or vertical direction and if they are reversed or not.
   * Accepts: 'row', 'row-reverse', 'column', 'column-reverse'.
   * NOTE: Using this prop can result in unexpected behavior with other props.
   * for example, justify and alignItems are reversed when used with column.
   * spacing, col breakpoints, and wrapping can also be affected.
   * @internal
   * @experimental
   */
  @Prop({ mutable: true }) direction: FluidFlexDirection =
    FluidFlexDirection.ROW;

  // ================================================================ //
  // -- Deprecated Props

  /**
   * DEPRECATED: "gutter" is now deprecated. Instead, use "removeSpacing"
   * Prop. -- Adds spacing between FluidGridItem children when true.
   * @deprecated
   */
  @Prop({ mutable: true }) gutter = true;
}
